import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { StartLoginService } from '@modules/api/start-login.service.generated';
import { CurrentLoginStylingService } from '@modules/common/services/current-login-styling.service';
import { DeviceTokenService } from '@modules/common/services/device-token.service';
import { isValidEmail } from '@modules/common/validators/is-valid-email';
import { CompleteLoginService } from '@modules/login-shared/services/complete-login.service';
import { LoginAccountLockoutComponent } from '@modules/login/components/login-account-lockout/login-account-lockout.component';
import { AccountLockoutComponentArgs } from '@modules/login/models/account-lockout-component-args';
import { CurrentLoginRequestService } from '@modules/login/services/current-login-request.service';

@Component({
    selector: 'app-login-main',
    templateUrl: './login-main.component.html',
    styleUrls: ['./login-main.component.scss']
})
export class LoginMainComponent implements OnInit {
    public form!: FormGroup;
    public invalidCredentials: boolean = false;
    public allowSignupLink: boolean = false;
    public invalidEmailAddress: boolean = false;

    private encryptedRedirectUrl?: string;

    constructor(
        private _formBuilder: FormBuilder,
        private _accountLockedDialog: MatDialog,
        private _router: Router,
        private _route: ActivatedRoute,
        private _startLoginService: StartLoginService,
        private _currentLoginRequestService: CurrentLoginRequestService,
        private _deviceTokenService: DeviceTokenService,
        private _currentLoginStylingService: CurrentLoginStylingService,
        private _completeLoginService: CompleteLoginService,
    ) { }

    ngOnInit() {
        this.form = this._formBuilder.group({
            'emailAddress': ['', Validators.required, isValidEmail],
            'password': ['', Validators.required]
        });

        this._currentLoginStylingService.configChanged.subscribe(result => {
            this.allowSignupLink = !!result && result.allowSignupLink;
        });
        this.encryptedRedirectUrl = this._route.snapshot.queryParams['redirect'];
        if (this.encryptedRedirectUrl)
            sessionStorage.setItem('titaniaRedirect', this.encryptedRedirectUrl);
    }

    public onSubmit(): void {
        this.form.patchValue({ 'emailAddress': this.form.get('emailAddress')!.value && (this.form.get('emailAddress')!.value).trim() });

        if (this.form.valid) {
            this.resetErrors();

            let deviceToken = this._deviceTokenService.getDeviceToken();
            this._startLoginService.startLoginRequest({
                emailAddress: this.form.get('emailAddress')!.value,
                password: this.form.get('password')!.value,
                deviceTokenId: deviceToken && deviceToken.id || undefined,
                deviceTokenToken: deviceToken && deviceToken.token || undefined,
                encryptedRedirectUrl: sessionStorage.getItem('titaniaRedirect') || undefined,
            }).subscribe(result => {
                if (result.lockedOutUntil) {
                    this.openAccountLockoutDialog(result.lockedOutUntil);
                } else if (result.isInvalidCredentials) {
                    this.invalidCredentials = true;
                } else if (result.loginRequestId) {
                    this._currentLoginRequestService.setCurrentLoginRequest({
                        id: result.loginRequestId,
                        token: result.loginRequestToken!
                    });
                    this._router.navigate(['./SelectMFADevice'], { relativeTo: this._route });
                } else if (result.passwordResetRequestId) {
                    //Password expired and needs to be changed
                    this._router.navigate(
                        ['./ResetPassword'],
                        {
                            relativeTo: this._route,
                            queryParams: {
                                requestId: result.passwordResetRequestId,
                                requestToken: result.passwordResetRequestToken,
                                requestReason: result.passwordResetRequestCreationReasonEnum,
                                ignoreMFA: result.shouldIgnoreMfa,
                            },
                        }
                    );
                } else {
                    this._completeLoginService.completeLogin({
                        redirectHTML: result.redirectHtml,
                        userSession: result.userSession
                    });
                }
            });
        }
    }

    public SetInvalidEmailAddress() {
        this.invalidEmailAddress = this.form.get('emailAddress')!.hasError('email');
    }

    private resetErrors(): void {
        this.invalidCredentials = false;
        this.invalidEmailAddress = false;
    }

    private openAccountLockoutDialog(lockedOutUntil: Date): void {
        this._accountLockedDialog.open(LoginAccountLockoutComponent, {
            data: <AccountLockoutComponentArgs>{
                lockedOutUntil: lockedOutUntil
            }
        });
    }
}
